<script>
  import { onMount } from 'svelte';
  import { editStore, imageStore, sessionStore } from '../stores';
  import { get } from 'svelte/store';
  import { loadTasksFromServer, saveTasksToServer, constructUrl, deleteHistory, selectResult } from '../utils/apiClient';
  import { downloadFile } from '../utils/imageUtils';
  import { createEventDispatcher } from 'svelte';
  import { fly } from 'svelte/transition';
  import { mergeHistory } from '../utils/galleryHistory';
  import { getWorkHistoryPage } from '../utils/apiClient';
  import PublishResult from '../components/PublishResult.svelte';
  import Modal from '../components/Modal.svelte';
  import ResultTools from '../components/ResultTools.svelte';

  const dispatch = createEventDispatcher();
  
  // 대시보드 작업 목록 (editStore에서 가져옴)
  $: dashboardTasks = $editStore.dashboardTasks;
  
  // 외부에서 대시보드에 작업 추가
  export function addTask(task) {
    editStore.update(store => ({
      ...store,
      dashboardTasks: [task, ...store.dashboardTasks]
    }));
  }
  
  let historyRows = [], nextCursor = null, hasMore = false, loadingHistory = false, historyError = '';
  let destroyed = false, galleryViewer = null, viewerZoom = 100, historyInitialized = false;
  async function loadHistory(older = false) {
    if (loadingHistory || !$sessionStore.userId) return;
    loadingHistory = true; historyError = '';
    const owner = $sessionStore.userId;
    try {
      const page = await getWorkHistoryPage(owner, { cursor: older ? nextCursor : null, limit: 30 });
      if (destroyed || owner !== $sessionStore.userId) return;
      const incoming = new Map(page.items.map(row => [String(row.process_id), row]));
      historyRows = [...page.items, ...historyRows.filter(row => !incoming.has(String(row.process_id)))];
      if (older || !historyInitialized) { nextCursor = page.next_cursor; hasMore = page.has_more; historyInitialized = true; }
      editStore.update(store => ({ ...store, dashboardTasks: mergeHistory(store.dashboardTasks, historyRows, constructUrl) }));
    } catch (e) { if (!destroyed) historyError = e.message; }
    finally { if (!destroyed) loadingHistory = false; }
  }
  onMount(() => {
    destroyed = false;
    const owner = $sessionStore.userId;
    (async () => {
      const webTasks = await loadTasksFromServer(owner, $sessionStore.sessionId);
      if (destroyed || owner !== $sessionStore.userId) return;
      editStore.update(store => {
        const currentIds = new Set(store.dashboardTasks.map(task => String(task.id)));
        return { ...store, dashboardTasks: [...store.dashboardTasks, ...webTasks.filter(task => !currentIds.has(String(task.id)))] };
      });
      await loadHistory();
    })();
    const timer = setInterval(() => { if (!document.hidden && $editStore.dashboardTasks.some(task => !task.completed && task.status !== 'failed')) loadHistory(); }, 10000);
    const recover = () => loadHistory(); document.addEventListener('serviceRecovered', recover); document.addEventListener('galleryChanged', recover);
    return () => { destroyed = true; clearInterval(timer); document.removeEventListener('serviceRecovered', recover); document.removeEventListener('galleryChanged', recover); };
  });

  // 작업 완료 알림 상태
  let notifications = [];

  // 결과 선택 모달 상태
  let selecting = false;
  let selectionModal = null; // { task, selectedIndex }

  function openSelectionModal(task) {
    selectionModal = { task, selectedIndex: 0 };
  }

  function closeSelectionModal() {
    selectionModal = null;
  }

  async function confirmSelection() {
    if (!selectionModal || selecting) return;
    selecting = true;
    const { task, selectedIndex } = selectionModal;
    const selectedUrl = task.resultImages[selectedIndex];
    const session = get(sessionStore);
    try {
      if (task.process_id && session.loggedIn && !session.isGuest) {
        const selected = await selectResult(task.process_id, selectedUrl, session.userId);
        if (selected.process_id) task.process_id = selected.process_id;
      }
      editStore.update(store => ({
        ...store,
        dashboardTasks: store.dashboardTasks.map(t =>
          t.id === task.id
            ? { ...t, process_id: task.process_id, resultImage: selectedUrl, resultImages: null, needsSelection: false }
            : t
        )
      }));
      // 선택 결과를 서버에도 저장하여 다음 방문 시에도 유지
      if (session.loggedIn && !session.isGuest) {
        const updatedTasks = get(editStore).dashboardTasks;
        saveTasksToServer(session.userId, session.sessionId, updatedTasks).catch(e =>
          console.error('작업 목록 저장 실패:', e)
        );
      }
      historyRows = []; nextCursor = null; historyInitialized = false;
      closeSelectionModal();
      loadHistory();
    } catch (e) {
      console.error('결과 선택 저장 실패:', e);
      alert('저장 중 오류가 발생했습니다.');
    } finally { selecting = false; }
  }

  function showNotification(message, undoCallback = null, duration = 4000) {
    const notification = {
      id: Date.now(),
      message,
      undoCallback
    };
    notifications = [...notifications, notification];

    setTimeout(() => {
      notifications = notifications.filter(n => n.id !== notification.id);
    }, duration);
  }

  function undoNotification(id) {
    const note = notifications.find(n => n.id === id);
    if (note && note.undoCallback) {
      note.undoCallback();
    }
    notifications = notifications.filter(n => n.id !== id);
  }

  function goToEditor() {
    window.history.pushState({}, '', '/editorv2');
    document.dispatchEvent(new CustomEvent('navigate', { detail: 'editorv2' }));
  }

  let deleting = false;
  async function deleteItems(tasks) {
    if (deleting || !tasks.length) return;
    deleting = true;
    const session = get(sessionStore);
    const ids = new Set(tasks.map(task => String(task.id)));
    const pids = new Set(tasks.flatMap(task => [task.process_id, ...(task.editHistory || []).map(item => item.processId)]).filter(Boolean).map(String));
    try {
      if (pids.size) await deleteHistory(session.userId, [...pids]);
      historyRows = historyRows.filter(row => !pids.has(String(row.process_id)));
      editStore.update(store => ({ ...store, dashboardTasks: store.dashboardTasks.filter(task => !ids.has(String(task.id))) }));
      await saveTasksToServer(session.userId, session.sessionId, get(editStore).dashboardTasks);
      showNotification(`${tasks.length}개 작업을 삭제했습니다.`);
    } catch (e) { historyError = `삭제 결과를 확인하지 못했습니다. ${e.message}`; }
    finally { deleting = false; }
  }
  async function deleteTask(taskId) {
    const task = dashboardTasks.find(task => task.id === taskId);
    if (task && confirm('이 작업을 갤러리에서 삭제할까요?')) await deleteItems([task]);
  }
  async function deleteAllTasks() {
    if (confirm(`현재 불러온 ${dashboardTasks.length}개 작업을 삭제할까요?`)) await deleteItems([...dashboardTasks]);
  }

  function viewInEditor(taskId) {
    const task = dashboardTasks.find(t => t.id === taskId);
    if (task && task.resultImage) {
      console.log("대시보드에서 작업 로드:", task);
      
      // 완전히 새로운 상태로 편집기 초기화
      editStore.update(store => ({
        ...store,
        selectedTab: 'edit',  // 결과 확인이 아닌 이미지 편집 섹션으로 이동
        currentTaskId: task.id,  // 현재 작업 ID 설정
        editHistory: [],  // 먼저 히스토리 비우기
        currentEditIndex: -1,
        selectedFeature: null  // 선택된 기능 초기화
      }));
      
      // 이미지 초기화
      imageStore.update(store => ({
        ...store,
        uploadedImage: null,
        uploadedImageUrl: constructUrl(task.originalImage),
        serverImageUrl: constructUrl(task.originalImage),
        resultImage: constructUrl(task.resultImage),
        resultImages: task.resultImages || null,
        resultFeature: task.featureId,
        resultOptions: task.options || task.editHistory?.at(-1)?.settings || {},
        currentTaskImageUrl: constructUrl(task.originalImage),  // 이 작업에 사용된 원본 이미지를 표시
        taskOriginalImageUrl: constructUrl(task.originalImage),
        canvasData: null  // 캔버스 데이터 초기화
      }));
      
      // 약간의 지연 후 히스토리 복원 (상태 초기화 후)
      setTimeout(() => {
        // 편집 히스토리 초기화 및 선택한 작업의 히스토리만 복원
        if (task.editHistory && task.editHistory.length > 0) {
          // 작업의 편집 히스토리를 전부 사용 (이미 이 작업에 대한 히스토리만 저장되어 있음)
          console.log("작업 히스토리 복원:", task.editHistory);
          
          // 깊은 복사로 객체 참조 문제 방지 및 URL 복원
          const taskHistory = JSON.parse(JSON.stringify(task.editHistory)).map(item => ({
            ...item,
            resultImage: constructUrl(item.resultImage)
          }));
          
          // 마지막 기능 가져오기
          const lastFeature = taskHistory.length > 0 ? taskHistory[taskHistory.length - 1].feature : null;
          
          editStore.update(store => ({
            ...store,
            // 전체 히스토리가 아닌 이 작업에만 해당하는 히스토리 복원
            editHistory: taskHistory,
            currentEditIndex: taskHistory.length - 1,  // 현재 편집 인덱스 설정
            selectedFeature: lastFeature  // 마지막 사용한 기능 선택
          }));
        }
      }, 100);  // 약간의 지연으로 상태 안정화
      
      // 대시보드에서 온 것을 표시하는 플래그 설정
      sessionStorage.setItem('fromDashboard', 'true');
      // 히스토리 분리를 위한 플래그 설정
      sessionStorage.setItem('currentTaskId', task.id.toString());
      window.currentTaskIdFromDashboard = task.id; // 디버깅용 전역 변수
      
      // 이동 경로를 편집기 V2로 변경
      window.history.pushState({}, '', '/editorv2');
      document.dispatchEvent(new CustomEvent('navigate', { detail: 'editorv2' }));
    }
  }

  onMount(() => {
    // 작업 완료 이벤트 리스너 등록
    const taskCompletedHandler = (event) => {
      const task = event.detail;
      
      // 작업 상태 업데이트
      editStore.update(store => ({
        ...store,
        dashboardTasks: store.dashboardTasks.map(t => 
          t.id === task.id ? { ...t, completed: true, resultImage: task.resultImage } : t
        )
      }));
      
      // 알림 표시
      showNotification(`${task.feature} 작업이 완료되었습니다!`);
    };
    
    document.addEventListener('taskCompleted', taskCompletedHandler);
    
    return () => {
      document.removeEventListener('taskCompleted', taskCompletedHandler);
    };
  });
</script>

<div class="dashboard-container">
  <h1 class="dashboard-title">갤러리 · 작업 대시보드</h1>
  <div class="gallery-toolbar"><p>앱과 웹에서 만든 작품을 함께 확인하세요.</p><button disabled={loadingHistory} on:click={() => loadHistory()}>새로고침</button></div>
  {#if loadingHistory}<p role="status">작품을 불러오는 중...</p>{/if}
  {#if historyError}<p role="alert">{historyError} <button on:click={() => loadHistory()}>다시 시도</button></p>{/if}
  {#if dashboardTasks.length > 0}
    <button disabled={deleting || loadingHistory} class="delete-all-btn" on:click={deleteAllTasks}>불러온 작업 삭제</button>
  {/if}
  
  {#if dashboardTasks.length === 0}
    <div class="empty-dashboard">
      <p>아직 작업중인게 없습니다, 편집을 시작해보세요!</p>
      <button class="gradient-button" on:click={goToEditor}>
        편집기로 이동
      </button>
    </div>
  {:else}
    <div class="tasks-grid">
      {#each dashboardTasks.filter(t => t.completed ? !!t.resultImage : true) as task (task.id)}
        <div class="task-card {task.completed ? (task.needsSelection ? 'needs-selection' : 'completed') : 'processing'}">
          <div class="task-header">
            <h3>{task.feature}</h3>
            <span class="task-status">
              {task.completed ? (task.needsSelection ? '선택 필요' : '완료됨') : task.status === 'failed' ? (task.coinRefunded ? '실패 · RION 반환됨' : '실패') : '처리중...'}
            </span>
          </div>
          
          <div class="task-image-container">
            <button class="open-artwork" disabled={!task.completed || task.needsSelection} aria-label="작품 크게 보기" on:click={() => { galleryViewer = task; viewerZoom = 100; }}>
              <img src={constructUrl(task.thumbnail || (task.completed && task.resultImage ? task.resultImage : task.originalImage))} alt="작업 이미지" class="task-image" loading="lazy" />
            </button>
            {#if !task.completed && task.status !== 'failed'}
              <div class="task-overlay">
                <div class="loader"></div>
              </div>
            {:else if task.needsSelection}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
              <div class="task-overlay selection-overlay" role="button" tabindex="0" on:click={() => openSelectionModal(task)}>
                <span class="selection-badge">🖼 사진 선택 필요</span>
              </div>
            {/if}
          </div>
          
          <div class="task-info">
            <p>시작 시간: {new Date(task.startTime).toLocaleString()}</p>
            {#if task.completed}
              <p>완료 시간: {task.endTime ? new Date(task.endTime).toLocaleString() : '-'}</p>
              {#if task.needsSelection}
                <div class="task-actions">
                  <button class="select-image-btn" on:click={() => openSelectionModal(task)}>
                    갤러리에 저장할 사진 선택
                  </button>
                </div>
              {:else}
                <div class="task-actions">
                  <button class="view-in-editor-btn" on:click={() => viewInEditor(task.id)}>
                    편집기에서 보기
                  </button>
                  <button class="download-history-btn" on:click={() => downloadFile(constructUrl(task.resultImage), `erion_history_${task.id}.png`)}>
                    다운로드
                  </button>
                  <PublishResult image={constructUrl(task.resultImage)} feature={task.featureId} options={task.options || task.editHistory?.at(-1)?.settings || {}} />
                </div>
              {/if}
            {/if}
            {#if task.error}<p role="status">{task.error}</p>{/if}
            <button disabled={deleting} class="delete-btn" on:click={() => deleteTask(task.id)}>삭제</button>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  {#if hasMore}<button class="load-more" disabled={loadingHistory} on:click={() => loadHistory(true)}>이전 작품 더 보기</button>{/if}
  {#if galleryViewer}
    <Modal label="갤러리 작품" on:close={() => galleryViewer = null}>
      <div class="gallery-viewer"><img src={constructUrl(galleryViewer.resultImage)} alt={galleryViewer.feature} style:width={`${viewerZoom}%`} /></div>
      <div class="gallery-toolbar">
        <button on:click={() => { const index = dashboardTasks.findIndex(task => task.id === galleryViewer.id); const previous = dashboardTasks.slice(0, index).reverse().find(task => task.completed && !task.needsSelection); if (previous) { galleryViewer = previous; viewerZoom = 100; } }}>이전 작품</button>
        <label>확대 <input type="range" min="100" max="250" step="25" bind:value={viewerZoom} /></label>
        <button on:click={() => { const index = dashboardTasks.findIndex(task => task.id === galleryViewer.id); const next = dashboardTasks.slice(index + 1).find(task => task.completed && !task.needsSelection); if (next) { galleryViewer = next; viewerZoom = 100; } else if (hasMore) loadHistory(true); }}>다음 작품</button>
      </div>
      <ResultTools image={constructUrl(galleryViewer.resultImage)} task={galleryViewer} />
    </Modal>
  {/if}
  <!-- 결과 선택 모달 -->
  {#if selectionModal}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div class="modal-backdrop" role="button" tabindex="-1" on:click={closeSelectionModal}>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
      <div class="selection-modal" role="dialog" aria-modal="true" on:click|stopPropagation>
        <h2 class="modal-title">갤러리에 저장할 사진을 선택하세요</h2>
        <p class="modal-desc">두 버전 중 하나만 갤러리에 저장됩니다.</p>
        <div class="modal-images">
          {#each selectionModal.task.resultImages as url, i}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
            <div
              class="modal-image-wrap {i === selectionModal.selectedIndex ? 'selected' : ''}"
              role="button"
              tabindex="0"
              on:click={() => selectionModal = { ...selectionModal, selectedIndex: i }}
            >
              <img src={constructUrl(url)} alt="버전 {i + 1}" class="modal-image"/>
              <div class="modal-image-label">버전 {i + 1}</div>
              {#if i === selectionModal.selectedIndex}
                <div class="modal-checkmark">✓</div>
              {/if}
            </div>
          {/each}
        </div>
        <div class="modal-actions">
          <button class="modal-cancel-btn" on:click={closeSelectionModal}>취소</button>
          <button disabled={selecting} class="modal-confirm-btn" on:click={confirmSelection}>
            선택한 버전 ({selectionModal.selectedIndex + 1}번) 저장
          </button>
        </div>
      </div>
    </div>
  {/if}
  
  <!-- 알림 표시 영역 -->
  <div class="notifications-container">
    {#each notifications as notification (notification.id)}
      <div class="notification" transition:fly={{ y: 20, duration: 300 }}>
        <span>{notification.message}</span>
        {#if notification.undoCallback}
          <button class="undo-btn" on:click={() => undoNotification(notification.id)}>취소</button>
        {/if}
      </div>
    {/each}
  </div>
</div>

<style>
  .gallery-toolbar { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; justify-content: space-between; margin: 16px 0; }
  .gallery-toolbar button, .load-more { font: inherit; padding: 9px 16px; border: 1px solid #8885; color: inherit; background: transparent; border-radius: 10px; cursor: pointer; }
  .load-more { display: block; margin: 24px auto; } .open-artwork { padding: 0; border: 0; background: transparent; width: 100%; height: 100%; cursor: zoom-in; }
  .gallery-viewer { overflow: auto; max-height: 65dvh; } .gallery-viewer img { display: block; max-width: none; }

  .dashboard-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem 1rem;
    position: relative;
  }

  .dashboard-title {
    text-align: center;
    margin-bottom: 2rem;
    font-size: 2.5rem;
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .empty-dashboard {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    text-align: center;
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .empty-dashboard p {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }

  :global(.dark) .empty-dashboard {
    background-color: rgba(255, 255, 255, 0.05);
  }

  :global(.light) .empty-dashboard {
    background-color: rgba(0, 0, 0, 0.05);
  }

  :global(.dark) .empty-dashboard p {
    color: rgba(255, 255, 255, 0.7);
  }

  :global(.light) .empty-dashboard p {
    color: rgba(0, 0, 0, 0.7);
  }

  .gradient-button {
    padding: 0.8rem 2rem;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(254, 106, 55, 0.3);
  }

  .gradient-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(254, 106, 55, 0.4);
  }

  .tasks-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }

  .task-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  :global(.dark) .task-card {
    background-color: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  :global(.light) .task-card {
    background-color: rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .task-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .task-card.completed {
    border-left: 4px solid #4CAF50;
  }

  .task-card.processing {
    border-left: 4px solid #FE6A37;
  }

  .task-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
  }

  :global(.dark) .task-header {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  :global(.light) .task-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .task-header h3 {
    margin: 0;
    font-size: 1rem;
  }

  .task-status {
    font-size: 0.8rem;
    padding: 0.2rem 0.6rem;
    border-radius: 10px;
    font-weight: 500;
  }

  .completed .task-status {
    background-color: rgba(76, 175, 80, 0.2);
    color: #4CAF50;
  }

  .processing .task-status {
    background-color: rgba(254, 106, 55, 0.2);
    color: #FE6A37;
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% { opacity: 0.7; }
    50% { opacity: 1; }
    100% { opacity: 0.7; }
  }

  .task-image-container {
    position: relative;
    width: 100%;
    height: 150px;
    overflow: hidden;
  }

  .task-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .task-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .selection-overlay {
    background-color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
    flex-direction: column;
    gap: 0.5rem;
  }

  .selection-badge {
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
    font-weight: 700;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
  }

  .task-card.needs-selection {
    border: 2px solid #FF6A00;
  }

  :global(.dark) .task-card.needs-selection {
    box-shadow: 0 4px 20px rgba(255, 106, 0, 0.3);
  }

  :global(.light) .task-card.needs-selection {
    box-shadow: 0 4px 20px rgba(255, 106, 0, 0.2);
  }

  .select-image-btn {
    margin-top: 0.5rem;
    padding: 0.6rem 1rem;
    border-radius: 8px;
    border: none;
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s;
    width: 100%;
  }

  .select-image-btn:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }

  /* 선택 모달 */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .selection-modal {
    background: #1e1e1e;
    border-radius: 16px;
    padding: 2rem;
    max-width: 560px;
    width: 90%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  :global(.light) .selection-modal {
    background: #fff;
  }

  .modal-title {
    font-size: 1.2rem;
    font-weight: 700;
    text-align: center;
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .modal-desc {
    text-align: center;
    font-size: 0.85rem;
    opacity: 0.7;
  }

  .modal-images {
    display: flex;
    gap: 1rem;
    justify-content: center;
  }

  .modal-image-wrap {
    flex: 1;
    max-width: 220px;
    border-radius: 10px;
    overflow: hidden;
    border: 2.5px solid transparent;
    cursor: pointer;
    position: relative;
    transition: border-color 0.18s, transform 0.15s;
  }

  .modal-image-wrap:hover {
    transform: scale(1.02);
  }

  .modal-image-wrap.selected {
    border-color: #FF6A00;
    box-shadow: 0 0 12px rgba(255, 106, 0, 0.5);
  }

  .modal-image {
    width: 100%;
    display: block;
    object-fit: cover;
  }

  .modal-image-label {
    text-align: center;
    font-size: 0.82rem;
    font-weight: 700;
    padding: 0.4rem 0;
    background: rgba(0, 0, 0, 0.4);
    color: white;
  }

  .modal-checkmark {
    position: absolute;
    top: 6px;
    right: 8px;
    background: #FF6A00;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
  }

  .modal-cancel-btn {
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    border: 1.5px solid rgba(255,255,255,0.2);
    background: transparent;
    color: inherit;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background 0.18s;
  }

  .modal-cancel-btn:hover {
    background: rgba(255,255,255,0.08);
  }

  .modal-confirm-btn {
    padding: 0.6rem 1.4rem;
    border-radius: 8px;
    border: none;
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.18s, transform 0.15s;
    box-shadow: 0 2px 8px rgba(255,106,0,0.35);
  }

  .modal-confirm-btn:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }

  .loader {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #FE6A37;
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .task-info {
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
  }

  .task-info p {
    margin: 0.3rem 0;
    font-size: 0.8rem;
  }

  :global(.dark) .task-info p {
    color: rgba(255, 255, 255, 0.7);
  }

  :global(.light) .task-info p {
    color: rgba(0, 0, 0, 0.7);
  }

  .view-in-editor-btn {
    margin-top: 0.5rem;
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    border: none;
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    color: white;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .view-in-editor-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }

  .view-in-editor-btn:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }

  .task-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .download-history-btn {
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    border: 1px solid #FE6A37;
    background: transparent;
    color: #FE6A37;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  :global(.dark) .download-history-btn {
    border-color: #FE6A37;
    color: #FE6A37;
  }

  .download-history-btn:hover {
    background: rgba(254, 106, 55, 0.1);
    transform: translateY(-3px);
  }

  .delete-btn {
    margin-top: 0.5rem;
    margin-left: auto;
    padding: 0.4rem 0.8rem;
    border-radius: 8px;
    border: none;
    background-color: #555;
    color: white;
    font-size: 0.8rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .delete-btn:hover {
    background-color: #777;
  }

  .delete-all-btn {
    display: block;
    margin-left: auto;
    margin-right: 0;
    margin-bottom: 1rem;
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    border: none;
    background-color: #ff5555;
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }

  .delete-all-btn:hover {
    background-color: #ff7777;
  }

  .undo-btn {
    margin-left: 1rem;
    padding: 0.3rem 0.8rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
  }

  :global(.dark) .undo-btn {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  :global(.light) .undo-btn {
    background: rgba(0, 0, 0, 0.05);
    color: #404040;
  }

  .notifications-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 1000;
  }

  .notification {
    background: linear-gradient(90deg, #FE6A37, #FF9A6E);
    color: white;
    padding: 0.8rem 1.5rem;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    animation: slideIn 0.3s ease;
  }

  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @media (max-width: 1024px) {
    .tasks-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  
  @media (max-width: 600px) {
    .tasks-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
